import { IPayloadProps } from "../../../../@types/Onboarding/IPayload";
import { IOptionProps } from "../../../../@types/reactSelect/IOption";
import { generateuuid } from "../../../../utils/uuid/generate";
import {
  AreaFormValueV2,
  FarmFormValueV2,
  MicroAreaFormValueV2,
  OptionV2,
} from "../../../../components/FormsV2";

export function toOption(value: any, label?: string): OptionV2 | null {
  if (value === undefined || value === null || value === "") return null;
  if (typeof value === "object" && "label" in value && "value" in value) return value as OptionV2;
  return { label: label || String(value), value };
}

export function optionFromList(options: OptionV2[], value: any) {
  return options.find((option) => String(option.value) === String(value)) || null;
}

export function coordsFromMap(mapCoords: any) {
  if (!mapCoords) return [];
  if (Array.isArray(mapCoords)) return mapCoords;
  if (Array.isArray(mapCoords.coords)) return mapCoords.coords;
  return [];
}

function toDayOption(value: any): OptionV2 | null {
  if (value === undefined || value === null || value === "") return null;

  if (typeof value === "object") {
    if ("label" in value && "value" in value) {
      const optionValue = value.value;
      const optionLabel = typeof value.label === "string" && value.label ? value.label : `${optionValue} Dias`;
      return { ...value, label: optionLabel, value: optionValue };
    }

    return null;
  }

  return { label: `${value} Dias`, value };
}

export function toFarmOptions(payload?: Partial<IPayloadProps>): OptionV2[] {
  return (payload?.farms || []).map((farm: any) => ({
    label: farm.name,
    value: farm.uuid || farm.id,
    info: farm,
  }));
}

export function toAreaOptions(payload?: Partial<IPayloadProps>, farmId?: string): OptionV2[] {
  return (payload?.areas || [])
    .filter((area: any) => !farmId || String(area.farm_id) === String(farmId))
    .map((area: any) => ({
      label: area.name || area.initials,
      value: area.uuid || area.id,
      info: area,
    }));
}

export function toUserOptions(payload?: Partial<IPayloadProps>, profileCode?: number): OptionV2[] {
  return (payload?.users || [])
    .filter((user: any) => !profileCode || Number(user.profile_id || user.profile?.value) === profileCode)
    .map((user: any) => ({ label: user.name, value: user.uuid || user.id, info: user }));
}

export function toFarmFormValue(farm?: any, managers: OptionV2[] = [], states: OptionV2[] = []): FarmFormValueV2 {
  const state = farm?.address?.state || farm?.address?.uf?.value;
  const cityCode = farm?.address?.city?.code || farm?.address?.city?.value;
  const cityName = farm?.address?.city?.name || farm?.address?.city?.label;

  return {
    id: farm?.id,
    uuid: farm?.uuid,
    name: farm?.name || "",
    initials: farm?.initials || "",
    document: farm?.document || "",
    state_registration: farm?.state_registration || "",
    manager_user_id: optionFromList(managers, farm?.manager_user_id) || toOption(farm?.manager_user_id),
    address: {
      state: optionFromList(states, state) || toOption(state),
      city: toOption(cityCode, cityName),
    },
    season: {
      rain_initial: Number(farm?.properties?.season?.rain_initial ?? farm?.season?.rain_initial ?? 3),
      dry_initial: Number(farm?.properties?.season?.dry_initial ?? farm?.season?.dry_initial ?? 8),
    },
    map_coords: coordsFromMap(farm?.map_coords || farm?.file?.coords),
  };
}

export function fromFarmFormValue(value: FarmFormValueV2) {
  return {
    uuid: value.uuid || generateuuid(),
    name: value.name,
    initials: value.initials,
    document: value.document || null,
    state_registration: value.state_registration || "",
    manager_user_id: String(value.manager_user_id?.value || ""),
    address: {
      city: {
        code: String(value.address.city?.value || ""),
        name: value.address.city?.label || "",
      },
      state: String(value.address.state?.value || ""),
      street: "",
    },
    properties: {
      season: value.season,
    },
    map_coords: value.map_coords || [],
    filename: value.name || "",
  };
}

export function toAreaFormValue(area?: any, farm?: OptionV2 | null, managers: OptionV2[] = []): AreaFormValueV2 {
  return {
    id: area?.id,
    uuid: area?.uuid,
    farm: farm || toOption(area?.farm_id),
    name: area?.name || "",
    initials: area?.initials || "",
    acreage: area?.acreage ? String(area.acreage) : "",
    indoor_grazing_time: toDayOption(area?.indoor_grazing_time),
    outdoor_grazing_time: toDayOption(area?.outdoor_grazing_time),
    manager_user_id: optionFromList(managers, area?.manager_user_id) || toOption(area?.manager_user_id),
    map_coords: coordsFromMap(area?.map_coords),
  };
}

export function fromAreaFormValue(value: AreaFormValueV2, farmId?: string) {
  return {
    uuid: value.uuid || generateuuid(),
    farm_id: farmId || value.farm?.value,
    name: value.name,
    initials: value.initials,
    acreage: value.acreage,
    manager_user_id: value.manager_user_id?.value || null,
    indoor_grazing_time: value.indoor_grazing_time,
    outdoor_grazing_time: value.outdoor_grazing_time,
    map_coords: {
      uuid: value.uuid || generateuuid(),
      coords: value.map_coords || [],
    },
  };
}

export function toMicroAreaFormValue(
  microArea?: any,
  farm?: OptionV2 | null,
  area?: OptionV2 | null
): MicroAreaFormValueV2 {
  return {
    id: microArea?.id,
    uuid: microArea?.uuid,
    farm: farm || null,
    area: area || toOption(microArea?.area_id),
    initials: microArea?.initials || "",
    acreage: microArea?.acreage ? String(microArea.acreage) : "",
    reason: toOption(microArea?.reason_id || microArea?.micro_area_reason_id),
    map_coords: coordsFromMap(microArea?.map_coords),
  };
}

export function fromMicroAreaFormValue(value: MicroAreaFormValueV2, areaId?: string) {
  return {
    uuid: value.uuid || generateuuid(),
    area_id: areaId || value.area?.value,
    initials: value.initials,
    acreage: Number(String(value.acreage).replace(",", ".")),
    reason_id: value.reason?.value,
    map_coords: {
      uuid: value.uuid || generateuuid(),
      coords: value.map_coords || [],
    },
  };
}

export function mergeById<T extends Record<string, any>>(current: T[] = [], incoming: T[], key: string) {
  const incomingKeys = incoming.map((item) => String(item[key]));
  return current.filter((item) => !incomingKeys.includes(String(item[key]))).concat(incoming);
}

export function mergeAreasForFarm(current: any[] = [], farmId: string, farmAreas: any[]) {
  return current.filter((area) => String(area.farm_id) !== String(farmId)).concat(farmAreas);
}

export function mergeMicroAreasForAreas(current: any[] = [], areaIds: string[], microAreas: any[]) {
  return current.filter((micro) => !areaIds.includes(String(micro.area_id))).concat(microAreas);
}

export function normalizeOptionForPayload(option: OptionV2 | null): IOptionProps | null {
  if (!option) return null;
  return { label: option.label, value: option.value, info: option.info };
}
